<script setup lang="ts">
    import type { RouteLocationRaw } from 'vue-router';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import ContactNameEmail from '@shared/components/ContactNameEmail.vue';
    import { formatDateTime, formatDate } from '@cwd/Shared/utils/formatDateTime';
    import TagsList from '@shared/components/Tags/TagsList.vue';
    import type { Contact } from '../Contact/ContactTypes';
    import type { AdditionalField } from './ListsTypes.d';
    import { Tag } from '@shared/components/Tags/TagsTypes';

    const props = defineProps<{
        contact: Contact;
        isBackOffice?: boolean;
        countFields?: { key: keyof Contact; label: string; timestampKey?: keyof Contact; route?: (contact: Contact) => RouteLocationRaw }[];
        selectedFields?: AdditionalField[];
        selectedTagIds?: string[];
        contactTo?: RouteLocationRaw;
        isAnswerInAutoFilter?: (value: string) => boolean;
        isAnswerInFilter?: (value: string) => boolean;
    }>();

    const emit = defineEmits<{
        bounceContact: [contactId: string];
        unsubscribeContact: [contactId: string];
        toggleTag: [tag: Tag];
        navigateToContact: [];
        toggleAnswerFilter: [value: string];
    }>();

    const unsubscribeReasonLabels: Record<number, string> = {
        1: $Resources.UnsubscribeReasonNoLongerWant,
        2: $Resources.UnsubscribeReasonTooFrequent,
        3: $Resources.UnsubscribeReasonNeverSignedUp,
        4: $Resources.UnsubscribeReasonInappropriate,
        5: $Resources.UnsubscribeReasonSpam,
        6: $Resources.UnsubscribeReasonOther,
    };

    function formatFieldValue(contact: Contact, field: AdditionalField): string {
        const val = (contact as any)[field.name];
        if (val == null || val === '') return $Resources.ContactFieldNoValue;
        if (field.type === 'datetime') return formatDate(val) || String(val);
        if (field.type === 'boolean') return val ? $Resources.CustomBoolDefaultValueTrue : $Resources.CustomBoolDefaultValueFalse;
        if (field.type === 'unsubscribe-reason') {
            const label = unsubscribeReasonLabels[val as number] ?? $Resources.UnsubscribeReasonUnknown;
            const text = (contact as any).UnsubscribeReasonText;
            return text ? `${label}: ${text}` : label;
        }
        if (field.type === 'bouncereason') return [val.BounceType, val.BounceCategory, val.BounceCode, val.BounceDiagnosis].filter(Boolean).join(', ');
        return String(val);
    }

    const GUID_EMPTY = '00000000-0000-0000-0000-000000000000';
</script>

<template>
    <div class="flex-1" style="min-width: min(400px, 100%)">
        <ContactNameEmail
            :contactId="contact.ContactId"
            :firstName="contact.FirstName"
            :lastName="contact.LastName"
            :email="contact.Email"
            :isActive="contact.IsActive"
            :hasBounced="contact.HasBounced"
            :smsNumber="contact.SmsNumber"
            :isSmsActive="contact.IsSmsActive"
            :hasSmsBounced="contact.HasSmsBounced"
            :to="contactTo"
            @navigate="$emit('navigateToContact')" />

        <TagsList v-if="contact.Tags?.length" :tags="contact.Tags" :selectedIds="selectedTagIds" @toggleTag="(tag) => emit('toggleTag', tag)" />

        <p class="meta" v-if="contact.ContactId !== GUID_EMPTY">
            <template v-if="contact.Created">
                <span class="glyphicon glyphicon-time"></span>
                {{ $Resources.CreatedLabel }}:
                <time :datetime="contact.Created">{{ formatDateTime(contact.Created) }}</time>
            </template>

            <template v-if="contact.LastModified">
                <i class="fa fa-pencil" aria-hidden="true"></i>
                {{ $Resources.LastModifiedLabel }}:
                <time :datetime="contact.LastModified">{{ formatDateTime(contact.LastModified) }}</time>
            </template>

            <template v-if="contact.AddedDateTime">
                <i class="fa fa-user-plus" aria-hidden="true"></i>
                {{ $Resources.AddedToListLabel }}:
                <time :datetime="contact.AddedDateTime">{{ formatDateTime(contact.AddedDateTime) }}</time>
            </template>

            <template v-if="contact.FirstResponded">
                <span class="glyphicon glyphicon-time"></span>
                {{ $Resources.FirstRespondedDateTimeLabel }}:
                <time :datetime="contact.FirstResponded">{{ formatDateTime(contact.FirstResponded) }}</time>
            </template>
        </p>

        <div v-if="isBackOffice && (contact.SentCount || contact.ReceivedCount)" class="form-group btn-group-sm d-flex gap-xs m-sm-t m-xs-b">
            <button class="btn btn-warning btn-xs" @click.stop="emit('bounceContact', contact.ContactId)">Bounce contact</button>
            <button class="btn btn-danger btn-xs" @click.stop="emit('unsubscribeContact', contact.ContactId)">Unsubscribe contact</button>
        </div>
    </div>

    <div style="min-width: min(240px, 100%)">
        <div class="d-flex col-gap-lg flex-wrap ug-values m-0-all">
            <template v-for="field in countFields" :key="field.key">
                <div v-if="contact[field.key]" class="ug-value">
                    <span class="ug-value-label">{{ field.label }}</span>
                    <span class="ug-value-indicator success m-sm-r"></span>
                    <router-link v-if="field.route" :to="field.route(contact)">{{ contact[field.key] }}</router-link>
                    <span v-else>{{ contact[field.key] }}</span>
                    <span class="d-block m-sm-t">
                        <time v-if="field.timestampKey && contact[field.timestampKey]" :datetime="contact[field.timestampKey] as string" class="ug-value-label">
                            {{ formatDateTime(contact[field.timestampKey] as string) }}
                        </time>
                    </span>
                </div>
            </template>
        </div>
    </div>

    <div class="w-full">
        <template v-if="selectedFields">
            <ul v-if="selectedFields?.length > 0" class="list-inline list-unstyled m-xs-t m-xs-b w-full">
                <li v-for="field in selectedFields" :key="field.name" class="m-xxs-b">
                    <span class="fw-600 m-xs-r">{{ field.listDisplayName || field.displayName || `[${field.name}]` }}:</span>
                    <span>{{ formatFieldValue(contact, field) }}</span>
                </li>
            </ul>
        </template>

        <template v-for="answer in contact.FreeTextAnswers" :key="answer.SurveyResponseId">
            <blockquote v-if="answer.Freetext" class="answer d-flex flex-wrap gap-sm justify-content-between">
                <p>
                    {{ answer.Freetext }}
                </p>
                <button
                    v-if="!isAnswerInAutoFilter?.(answer.Freetext)"
                    type="button"
                    class="btn btn-default btn-xs m-auto-l text-noitalic hidden-print"
                    @click="emit('toggleAnswerFilter', answer.Freetext)">
                    <template v-if="!isAnswerInFilter?.(answer.Freetext)">
                        <span class="glyphicon glyphicon-filter"></span>
                        {{ $Resources.SurveyAddQuestionOptionFilter }}
                    </template>
                    <template v-else>
                        <span class="glyphicon glyphicon-remove"></span>
                        {{ $Resources.SurveyRemoveQuestionOptionFilter }}
                    </template>
                </button>
            </blockquote>
        </template>

        <BsAlert class="m-xs-t m-0-b" :show="!!(contact.BounceCategory || contact.BounceDiagnosis)" :type="AlertTypes.Warning" :dismissible="false">
            <template #content>
                <strong>
                    <span class="text-uppercase-first d-inline-block">{{ contact.BounceCategory }}</span>
                    <span v-if="contact.HardBounce">({{ $Resources.HardBounce }})</span>
                    <span v-else>({{ $Resources.SoftBounce }})</span>
                </strong>
                {{ contact.BounceDiagnosis }}
            </template>
        </BsAlert>
    </div>
</template>
